/**
 * useChartData.hook.ts
 *
 * Custom React hook to transform raw feature data and selected feature names
 * into an array of IChartProps for rendering line charts with @fluentui/react-charting.
 *
 * - Accepts:
 *    - rawFeatureData: Array of FeaturesData objects, each containing feature time series data.
 *    - selectedFeatures: Array of feature names to include in the chart.
 *
 * - Returns:
 *    - Array of IChartProps, each representing chart data for a set of features.
 *
 * - Main logic:
 *    - If no features are selected or no data is present, logs an event and returns an empty array.
 *    - For each FeaturesData object:
 *        - Filters features to only those selected.
 *        - Maps each feature to a line chart data object:
 *            - Converts each data point's date and value.
 *            - Prepares callout data for tooltips.
 *            - Assigns a color and line style.
 *        - Logs an event if any features are processed.
 *    - Logs a summary event after processing all data.
 *    - Uses useMemo to optimize performance and avoid unnecessary recalculations.
 */
import { getNextColor, IChartProps } from "@fluentui/react-charting";
import { useMemo } from "react";
import { logEvent } from "../../../../helper/AppInsightsHelper";
import { FeaturesData } from "../ExploreFeatures.interfaces";

export const useChartData = (rawFeatureData: FeaturesData[], selectedFeatures: string[]): IChartProps[] =>
  useMemo<IChartProps[]>(() => {
    if (!selectedFeatures.length || !rawFeatureData.length) {
      logEvent("Product Telemetry", "Customer Page", "ExploreFeatures", {
        eventName: "ChartData_EmptySelectionOrData",
        hookName: "useChartData",
        selectedFeaturesCount: selectedFeatures.length,
        rawFeatureDataCount: rawFeatureData.length
      });
      return [];
    }

    const chartData: IChartProps[] = rawFeatureData.map((featuresData) => {
      const filteredFeatures = (featuresData.data ?? []).filter((feature) => selectedFeatures.includes(feature.name));

      const lineChartData = filteredFeatures.map((feature, idx) => ({
        legend: feature.name,
        data: feature.data.map((dataPoint) => {
          const date = new Date(dataPoint.snapshotDate);
          const value = Number(dataPoint.currentValue);
          return {
            x: date,
            y: value,
            xAxisCalloutData: date.toLocaleDateString(),
            yAxisCalloutData: value.toLocaleString()
          };
        }),
        color: getNextColor(idx),
        lineOptions: { lineBorderWidth: "3" }
      }));

      if (filteredFeatures.length > 0) {
        logEvent("Product Telemetry", "Customer Page", "ExploreFeatures", {
          eventName: "ChartData_FeatureProcessed",
          hookName: "useChartData",
          featureCount: filteredFeatures.length,
          featureNames: filteredFeatures.map((f) => f.name)
        });
      }

      return { lineChartData } as IChartProps;
    });

    logEvent("Product Telemetry", "Customer Page", "ExploreFeatures", {
      eventName: "ChartData_Generated",
      hookName: "useChartData",
      chartCount: chartData.length,
      selectedFeaturesCount: selectedFeatures.length
    });

    return chartData;
  }, [rawFeatureData, selectedFeatures]);
